import React from 'react';
import Slider from '@material-ui/core/Slider';
import styled from 'styled-components';

import type { PatientChatResponseDetail } from '@mummssoftware/utils';

import { RESPONSE_DETAIL_LEVELS, responseDetailIndex } from '../../constants';
import defaults from '../../translations/en.json';

const LEVEL_LABELS: Record<PatientChatResponseDetail, string> = {
  brief: defaults.detailBrief,
  succinct: defaults.detailSuccinct,
  balanced: defaults.detailBalanced,
  detailed: defaults.detailDetailed,
  verbose: defaults.detailVerbose,
};

export const levelLabel = (index: number): string =>
  LEVEL_LABELS[RESPONSE_DETAIL_LEVELS[index]] || '';

/** 'Verbose' for 'verbose'; '' for a level this widget does not know. */
export const responseDetailLabel = (
  detail: string | null | undefined,
): string =>
  RESPONSE_DETAIL_LEVELS.includes(detail as PatientChatResponseDetail)
    ? LEVEL_LABELS[detail as PatientChatResponseDetail]
    : '';

type DetailSliderProps = {
  value: PatientChatResponseDetail;
  onChange: (detail: PatientChatResponseDetail) => void;
  disabled?: boolean;
  /** Dashboard tile: narrower track, no Brief/Verbose captions (35px header). */
  compact?: boolean;
};

const Root = styled.div<{ $compact?: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  flex: none;
`;

const Caption = styled.span`
  font-size: 9.5px;
  line-height: 1;
  letter-spacing: 0.02em;
  white-space: nowrap;
  color: ${(props) =>
    props.theme.palette.text.readOnly || props.theme.palette.text.secondary};
`;

const Track = styled.div<{ $compact?: boolean }>`
  width: ${(props) => (props.$compact ? 84 : 110)}px;
  display: flex;
  align-items: center;
`;

const StyledSlider = styled(Slider)`
  && {
    padding: 9px 0;
  }
  && .MuiSlider-rail,
  && .MuiSlider-track {
    height: 3px;
  }
  && .MuiSlider-mark {
    width: 3px;
    height: 3px;
    border-radius: 50%;
  }
  && .MuiSlider-thumb {
    width: 12px;
    height: 12px;
    margin-top: -5px;
    margin-left: -6px;
  }
`;

const toIndex = (value: number | number[]) =>
  Array.isArray(value) ? value[0] : value;

/** Five-step Brief → Verbose control, sent to the agent as responseDetail. */
export const DetailSlider = ({
  value,
  onChange,
  disabled,
  compact,
}: DetailSliderProps) => (
  <Root $compact={compact} data-testid="maia-detail-slider">
    {!compact ? (
      <Caption data-testid="maia-detail-caption">
        {defaults.detailBrief}
      </Caption>
    ) : null}
    <Track $compact={compact}>
      <StyledSlider
        value={responseDetailIndex(value)}
        min={0}
        max={RESPONSE_DETAIL_LEVELS.length - 1}
        step={1}
        marks
        disabled={disabled}
        valueLabelDisplay="auto"
        valueLabelFormat={levelLabel}
        getAriaValueText={levelLabel}
        aria-label={defaults.detailLevel}
        onChange={(_event, next) =>
          onChange(RESPONSE_DETAIL_LEVELS[toIndex(next)])
        }
      />
    </Track>
    {!compact ? (
      <Caption data-testid="maia-detail-caption">
        {defaults.detailVerbose}
      </Caption>
    ) : null}
  </Root>
);

export default DetailSlider;
